import Footer from 'components/Footer'
import RootWrapper from 'pages/utils/RootWrapper'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import HyperCatCrying from './hypercat-crying.png'
import { ButtonPrimary } from 'components/Button'
import { LINKS } from 'constants/links'
import { Helmet } from 'react-helmet'
import { useActiveWeb3React } from 'hooks'

// Styled Components
const MaintenanceWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  position: relative;
  padding: 16px;
`

const MaintenanceNote = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 600px;

  h1 {
    font-family: 'unbounded';
  }

  p {
    font-size: 14px;
    text-align: center;
    color: ${({ theme }) => theme.text2};
    margin-bottom: 12px;
  }
  button {
    max-width: 200px;
  }

  .error-code {
    font-size: 12px;
    color: ${({ theme }) => theme.text3};
    background: ${({ theme }) => theme.bg2};
    padding: 8px;
    border-radius: 4px;
    word-break: break-all;
    margin-top: 12px;
    margin-bottom: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
  }
`

// Utility to generate the error code
const generateErrorCode = (account: string | null | undefined) => {
  const walletAddress = account ? account : 'N/A'

  const timestamp = new Date().toISOString()
  const rawData = `wallet:${walletAddress}|timestamp:${timestamp}`

  // Base64 encoding
  return btoa(rawData)
}

const ErrorPage = () => {
  const { account } = useActiveWeb3React()

  const [errorCode, setErrorCode] = useState('Generating...')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    setErrorCode(generateErrorCode(account))
  }, [account])

  const handleCopy = () => {
    navigator.clipboard
      .writeText(errorCode)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 3000)
      })
      .catch((err) => console.error('Failed to copy:', err))
  }

  return (
    <>
      <Helmet>
        <title>Error | HyperSwap Exchange on Hyperliquid</title>
        <meta
          name="description"
          content="An error has occurred. We will be back online shortly to provide you with the best decentralized exchange experience."
        />
        <meta property="og:title" content="404 Error | HyperSwap Exchange on Hyperliquid" />
        <meta property="og:url" content={`${window.location.origin}`} />
      </Helmet>
      <RootWrapper>
        <MaintenanceWrapper>
          <MaintenanceNote>
            <img src={HyperCatCrying} alt="loading failed" style={{ width: '200px', borderRadius: '10000px' }} />
            <h1 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#4afc38"
                strokeWidth="2"
                className="icon icon-tabler icons-tabler-outline icon-tabler-alert-triangle"
                style={{ verticalAlign: 'middle', marginRight: '6px' }}
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M12 9v4" />
                <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
                <path d="M12 16h.01" />
              </svg>
              Error Notice
            </h1>
            <p>
              We’re sorry, but an error has occurred, and you’ve landed on this page. <br />
              <br />
              Please let us know what actions you were performing before encountering this error by joining our Discord.{' '}
              If you need assistance, please create a support ticket in our Discord and include the error code below.
              Just click on the code to copy it easily!
              <br />
              <br />
              While we work on resolving the issue, feel free to reach out to us on Discord for updates and to share
              more details about the error. Thank you for your understanding and patience!
            </p>

            <div className="error-code" onClick={handleCopy}>
              <strong>
                Error Code: {errorCode}{' '}
                {copied ? (
                  <span style={{ marginLeft: '4px', color: 'white' }}>Copied!</span>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-copy"
                    style={{ marginLeft: '2px', verticalAlign: 'middle' }}
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M7 7m0 2.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z" />
                    <path d="M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1" />
                  </svg>
                )}
              </strong>
            </div>

            <ButtonPrimary onClick={() => window.open(LINKS.discord.link, '_blank')}>Join Discord</ButtonPrimary>
          </MaintenanceNote>
        </MaintenanceWrapper>
      </RootWrapper>
      <Footer />
    </>
  )
}

export default ErrorPage
